import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BellRing, Plus, CheckCircle2, AlertTriangle, Clock, ExternalLink, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const EMPTY = {
    kind: "Passport", label: "", expiry_date: "", cost: "", vendor: "",
    auto_renew: false, notes: "",
};

const KIND_ICONS = {
    Card: "💳", Subscription: "📱", Warranty: "🛡", AppleCare: "🍎",
    Insurance: "🩺", Passport: "🛂", Visa: "✈️", "Driving Licence": "🚗",
    Domain: "🌐", SSL: "🔒", AMC: "🔧", Membership: "🎖",
    SIM: "📶", "Vehicle Insurance": "🚙", "Vehicle PUC": "♻️",
    "Vehicle FASTag": "🛣", "Vehicle Registration": "📄",
};

function statusChip(comp) {
    const map = {
        expired: { label: "Expired", cls: "bg-red-100 text-red-800 border-red-200" },
        expires_today: { label: "Today", cls: "bg-red-50 text-red-700 border-red-200" },
        urgent: { label: "≤ 7 days", cls: "bg-amber-50 text-amber-800 border-amber-200" },
        upcoming: { label: "≤ 30 days", cls: "bg-sky-50 text-sky-700 border-sky-200" },
        future: { label: "Future", cls: "bg-stone-50 text-stone-600 border-stone-200" },
        unknown: { label: "—", cls: "bg-stone-50 text-stone-500 border-stone-200" },
    };
    const s = map[comp] || map.unknown;
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
}

function Bucket({ title, icon: Icon, tone, list, canEdit, onRenewed, onRemove, onEdit, linkFor }) {
    if (list.length === 0) return null;
    return (
        <div className="rounded-lg border border-stone-200 bg-white p-5 mb-4" data-testid={`bucket-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={14} className={`text-${tone}-700`} />
                <div className="text-sm font-medium text-stone-900">{title}</div>
                <div className="text-[11px] text-stone-500">· {list.length}</div>
            </div>
            <div className="divide-y divide-stone-100">
                {list.map((it) => (
                    <div key={it.id} className="py-2.5 flex items-center gap-3" data-testid={`renewal-row-${it.id}`}>
                        <div className="text-lg leading-none w-6 text-center">{KIND_ICONS[it.kind] || "📌"}</div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-stone-900 truncate">{it.label}</div>
                            <div className="text-[11px] text-stone-500">
                                {it.kind} · {fmtDate(it.expiry_date)}
                                {it.days_until != null && (
                                    <span className={`ml-1 ${it.days_until < 0 ? "text-red-700" : "text-stone-500"}`}>
                                        · {it.days_until < 0 ? `${Math.abs(it.days_until)}d overdue` : `in ${it.days_until}d`}
                                    </span>
                                )}
                                {it.source === "auto" && <span className="ml-1 text-stone-400">· auto</span>}
                            </div>
                        </div>
                        {statusChip(it.computed_status)}
                        {linkFor(it) && (
                            <Link to={linkFor(it)} className="text-stone-500 hover:text-stone-900 text-[12px] inline-flex items-center gap-0.5" aria-label="Open source">
                                <ExternalLink size={12} />
                            </Link>
                        )}
                        {canEdit && it.source !== "auto" && (
                            <>
                                <button className="text-[11px] text-stone-600 hover:text-stone-900 border border-stone-200 rounded px-2 py-1" onClick={() => onRenewed(it)} data-testid={`renewal-renewed-${it.id}`}>
                                    Renewed
                                </button>
                                <button className="text-stone-400 hover:text-stone-900 p-1" onClick={() => onEdit(it)} title="Edit" data-testid={`renewal-edit-${it.id}`}>
                                    <Pencil size={11} />
                                </button>
                                <button className="text-stone-400 hover:text-red-600 text-[11px] px-1" onClick={() => onRemove(it)} aria-label="Archive" data-testid={`renewal-delete-${it.id}`}>×</button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}


export default function Renewals() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const [items, setItems] = useState([]);
    const [kinds, setKinds] = useState([]);
    const [days, setDays] = useState(90);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/renewals/upcoming?days=${days}`);
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, [days]);

    useEffect(() => {
        api.get("/renewals/meta/kinds").then((r) => setKinds(r.data?.kinds || []));
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            if (!body.label && body.kind) body.label = body.kind;
            if (body.cost === "" || body.cost == null) delete body.cost; else body.cost = Number(body.cost);
            if (editingId) {
                await api.patch(`/renewals/${editingId}`, body);
                toast.success("Renewal updated");
            } else {
                await api.post("/renewals", body);
                toast.success("Renewal added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (item) => {
        if (item.source === "auto") { toast.info("Auto-mirrored renewals are edited via their parent record."); return; }
        setEditingId(item.id);
        setF({
            kind: item.kind || "Passport",
            label: item.label || "",
            expiry_date: (item.expiry_date || "").slice(0, 10),
            cost: item.cost ?? "",
            vendor: item.vendor || "",
            auto_renew: !!item.auto_renew,
            notes: item.notes || "",
        });
        setOpen(true);
    };

    const markRenewed = async (item) => {
        if (item.source === "auto") {
            toast.info("Auto-mirrored renewals renew via their parent record (asset / card / subscription).");
            return;
        }
        try {
            await api.post(`/renewals/${item.id}/renewed`);
            toast.success("Renewed +365 days");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (item) => {
        if (item.source === "auto") return;
        if (!window.confirm("Archive this renewal?")) return;
        try { await api.delete(`/renewals/${item.id}`); toast.success("Archived"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    // Group by status bucket for the wow layout
    const buckets = useMemo(() => {
        const b = { expired: [], expires_today: [], urgent: [], upcoming: [], future: [] };
        items.forEach((it) => (b[it.computed_status] || b.future).push(it));
        return b;
    }, [items]);

    const linkFor = (it) => {
        if (it.entity_type === "card") return `/cards/${it.entity_id}`;
        if (it.entity_type === "asset") return `/assets/${it.entity_id}`;
        if (it.entity_type === "subscription") return `/subscriptions`;
        return null;
    };

    return (
        <div data-testid="renewals-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><BellRing size={12} /> Renewal Engine</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Everything that expires.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Card expiries, warranties, AppleCare, insurance, passports, visas, AMCs, domains &mdash;
                        one place with 30 / 7 / 1-day nudges before the deadline.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                        <SelectTrigger className="w-36" data-testid="renewals-horizon"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="30">Next 30 days</SelectItem>
                            <SelectItem value="90">Next 90 days</SelectItem>
                            <SelectItem value="180">Next 6 months</SelectItem>
                            <SelectItem value="365">Next 12 months</SelectItem>
                        </SelectContent>
                    </Select>
                    {canEdit && (
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                            <DialogTrigger asChild>
                                <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="renewal-add" onClick={openAdd}>
                                    <Plus size={14} className="mr-1.5" /> Add renewal
                                </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="renewal-add-dialog">
                                <DialogHeader>
                                    <DialogTitle className="font-display text-2xl">{editingId ? "Edit renewal" : "New renewal"}</DialogTitle>
                                    <DialogDescription className="text-[13px] text-stone-500">
                                        Add anything with an expiry &mdash; passport, visa, AMC, domain, SSL, membership.
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={submit} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="eyebrow">Kind</Label>
                                            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {kinds.map((k) => <SelectItem key={k} value={k}>{KIND_ICONS[k] || "📌"} {k}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Expiry date</Label>
                                            <Input type="date" required value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="mt-1.5" data-testid="renewal-expiry" />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="eyebrow">Label</Label>
                                            <Input required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className="mt-1.5" placeholder={`e.g. My ${f.kind}`} data-testid="renewal-label" />
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Vendor / provider</Label>
                                            <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Cost (optional)</Label>
                                            <Input type="number" step="0.01" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1.5 font-mono-figure" />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="eyebrow">Notes</Label>
                                            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" />
                                        </div>
                                    </div>
                                    <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="renewal-submit">
                                        {busy ? "Saving…" : editingId ? "Save changes" : "Add renewal"}
                                    </Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {items.length === 0 && (
                <div className="rounded-lg border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500 text-sm" data-testid="renewals-empty">
                    <BellRing size={18} className="mx-auto mb-2 text-stone-400" />
                    Nothing coming up in the next {days} days. Add a passport or AMC to get reminders.
                </div>
            )}

            <Bucket title="Expired" icon={AlertTriangle} tone="red" list={buckets.expired} canEdit={canEdit} onRenewed={markRenewed} onRemove={remove} onEdit={openEdit} linkFor={linkFor} />
            <Bucket title="Expires today" icon={AlertTriangle} tone="red" list={buckets.expires_today} canEdit={canEdit} onRenewed={markRenewed} onRemove={remove} onEdit={openEdit} linkFor={linkFor} />
            <Bucket title="Next 7 days" icon={Clock} tone="amber" list={buckets.urgent} canEdit={canEdit} onRenewed={markRenewed} onRemove={remove} onEdit={openEdit} linkFor={linkFor} />
            <Bucket title="Next 30 days" icon={Clock} tone="sky" list={buckets.upcoming} canEdit={canEdit} onRenewed={markRenewed} onRemove={remove} onEdit={openEdit} linkFor={linkFor} />
            <Bucket title="Beyond 30 days" icon={CheckCircle2} tone="emerald" list={buckets.future} canEdit={canEdit} onRenewed={markRenewed} onRemove={remove} onEdit={openEdit} linkFor={linkFor} />
        </div>
    );
}
